/**
 * C4 — oversharing readiness scoring (the flagship number). Pure, deterministic
 * functions over the scan signals, so the score is explainable and testable.
 *
 * Score starts at 100 and is reduced by weighted penalties for broadly-scoped
 * sharing and for missing classification. Every deduction is surfaced as a
 * finding with a severity, so the number is never a black box.
 */
import type { Severity } from '@/components/SeverityChip';
import type { SiteRef, SiteSharingSignal } from '@/graph/modules/sites';

export interface Finding {
  id: string;
  severity: Severity;
  title: string;
  detail: string;
  webUrl?: string;
}

export interface ReadinessResult {
  score: number;
  sitesScanned: number;
  sitesWithAnonymous: number;
  sitesWithOrgWide: number;
  labelCount: number | null;
  findings: Finding[];
  siteSource: 'getAllSites' | 'search';
}

const WEIGHT_ANON = 8; // per site with an "Anyone" link (critical)
const WEIGHT_ORG = 3; // per site with an org-wide link (warning)
const PENALTY_NO_LABELS = 15; // no published sensitivity labels at all

export interface SiteScanRecord {
  site: SiteRef;
  signal: SiteSharingSignal;
}

export function scoreReadiness(
  records: SiteScanRecord[],
  labelCount: number | null,
  siteSource: 'getAllSites' | 'search',
): ReadinessResult {
  const findings: Finding[] = [];
  let deductions = 0;
  let sitesWithAnonymous = 0;
  let sitesWithOrgWide = 0;

  for (const { site, signal } of records) {
    if (signal.anonymousLinks > 0) {
      sitesWithAnonymous += 1;
      deductions += WEIGHT_ANON;
      findings.push({
        id: `${site.id}:anon`,
        severity: 'critical',
        title: site.displayName,
        detail: `${signal.anonymousLinks} "Anyone" (anonymous) sharing link${signal.anonymousLinks > 1 ? 's' : ''} — visible to anyone with the URL and indexable by Copilot.`,
        webUrl: site.webUrl,
      });
    }
    if (signal.orgWideLinks > 0) {
      sitesWithOrgWide += 1;
      deductions += WEIGHT_ORG;
      findings.push({
        id: `${site.id}:org`,
        severity: 'warn',
        title: site.displayName,
        detail: `${signal.orgWideLinks} organisation-wide sharing link${signal.orgWideLinks > 1 ? 's' : ''} — reachable by every user, widening Copilot's grounding surface.`,
        webUrl: site.webUrl,
      });
    }
  }

  if (labelCount === 0) {
    deductions += PENALTY_NO_LABELS;
    findings.push({
      id: 'labels:none',
      severity: 'warn',
      title: 'No sensitivity labels published',
      detail: 'Content cannot be classified or protected, so oversharing cannot be contained by policy. Publish Purview sensitivity labels.',
    });
  }

  if (findings.length === 0) {
    findings.push({
      id: 'clean',
      severity: 'pass',
      title: 'No broadly-scoped sharing detected',
      detail: 'None of the scanned sites expose "Anyone" or organisation-wide links. Re-scan periodically as sharing changes.',
    });
  }

  const score = Math.max(0, Math.min(100, 100 - deductions));

  // Critical first, then warnings, then passes.
  const order: Record<Severity, number> = { critical: 0, warn: 1, info: 2, pass: 3 };
  findings.sort((a, b) => order[a.severity] - order[b.severity]);

  return {
    score,
    sitesScanned: records.length,
    sitesWithAnonymous,
    sitesWithOrgWide,
    labelCount,
    findings,
    siteSource,
  };
}

export function gradeLabel(score: number): string {
  if (score >= 80) return 'Ready — low oversharing exposure';
  if (score >= 50) return 'At risk — remediate broadly-shared sites before enabling Copilot broadly';
  return 'Not ready — significant oversharing exposure';
}
